import React, { useState } from 'react';
import { HighlightTooltip } from '@/components/highlight';

const placements = [
  'top',
  'top-start',
  'top-end',
  'right',
  'right-start',
  'right-end',
  'bottom',
  'bottom-start',
  'bottom-end',
  'left',
  'left-start',
  'left-end',
] as const;

const colors = [
  'bg-blue-100 text-blue-800',
  'bg-green-100 text-green-800',
  'bg-yellow-100 text-yellow-800',
  'bg-red-100 text-red-800',
  'bg-purple-100 text-purple-800',
  'bg-pink-100 text-pink-800',
];

const TooltipDemo = () => {
  const [showArrow, setShowArrow] = useState(true);
  const [delayShow, setDelayShow] = useState(200);
  const [delayHide, setDelayHide] = useState(200);
  const [offsetDistance, setOffsetDistance] = useState(8);

  return (
    <div className="container mx-auto p-8">
      <h1 className="text-3xl font-bold mb-6">Tooltip Component Demo</h1>
      
      <div className="mb-8 p-4 bg-gray-50 rounded-lg">
        <h2 className="text-xl font-semibold mb-4">Configure Options</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block mb-2">
              <input
                type="checkbox"
                checked={showArrow}
                onChange={(e) => setShowArrow(e.target.checked)}
                className="mr-2"
              />
              Show Arrow
            </label>
          </div>
          <div>
            <label className="block mb-2">
              Show Delay (ms):
              <input
                type="range"
                min="0"
                max="1000"
                step="50"
                value={delayShow}
                onChange={(e) => setDelayShow(Number(e.target.value))}
                className="w-full"
              />
              <span className="text-sm text-gray-600">{delayShow}ms</span>
            </label>
          </div>
          <div>
            <label className="block mb-2">
              Hide Delay (ms):
              <input
                type="range"
                min="0"
                max="1000"
                step="50"
                value={delayHide}
                onChange={(e) => setDelayHide(Number(e.target.value))}
                className="w-full"
              />
              <span className="text-sm text-gray-600">{delayHide}ms</span>
            </label>
          </div>
          <div>
            <label className="block mb-2">
              Offset Distance (px):
              <input
                type="range"
                min="0"
                max="24"
                step="1"
                value={offsetDistance}
                onChange={(e) => setOffsetDistance(Number(e.target.value))}
                className="w-full"
              />
              <span className="text-sm text-gray-600">{offsetDistance}px</span>
            </label>
          </div>
        </div>
      </div>

      <h2 className="text-2xl font-semibold mb-4">Placement Options</h2>
      
      <div className="grid grid-cols-2 md:grid-cols-3 gap-12 mb-12">
        {placements.map((placement, index) => (
          <div key={placement} className="flex flex-col items-center justify-center h-40">
            <HighlightTooltip
              content={
                <div className={`p-2 rounded ${colors[index % colors.length]}`}>
                  This tooltip uses <span className="font-bold">{placement}</span> placement
                </div>
              }
              position={placement}
              showArrow={showArrow}
              delayShow={delayShow}
              delayHide={delayHide}
              offsetDistance={offsetDistance}
            >
              <button className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-md">
                {placement}
              </button>
            </HighlightTooltip>
          </div>
        ))}
      </div>

      <h2 className="text-2xl font-semibold mb-4">Content Examples</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
        <div className="border p-4 rounded-lg">
          <h3 className="font-semibold mb-4">Basic Tooltip</h3>
          <div className="flex justify-center">
            <HighlightTooltip
              content="This is a simple text tooltip"
              showArrow={showArrow}
              delayShow={delayShow}
              delayHide={delayHide}
            >
              <button className="px-4 py-2 bg-blue-500 text-white rounded-md">
                Hover me
              </button>
            </HighlightTooltip>
          </div>
        </div>
        
        <div className="border p-4 rounded-lg">
          <h3 className="font-semibold mb-4">Rich Content Tooltip</h3>
          <div className="flex justify-center">
            <HighlightTooltip
              content={
                <div className="p-2 max-w-xs">
                  <h4 className="font-bold text-lg">Rich Content</h4>
                  <p className="mb-2">Tooltips can contain rich HTML content with formatting.</p>
                  <ul className="list-disc pl-5">
                    <li>Support for lists</li>
                    <li>Images and icons</li>
                    <li>Any React component</li>
                  </ul>
                </div>
              }
              showArrow={showArrow}
              delayShow={delayShow}
              delayHide={delayHide}
            >
              <button className="px-4 py-2 bg-purple-500 text-white rounded-md">
                Rich Content
              </button>
            </HighlightTooltip>
          </div>
        </div>
      </div>
      
      <h2 className="text-2xl font-semibold mb-4">Edge Case Testing</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
        <div className="border p-4 rounded-lg">
          <h3 className="font-semibold mb-4">Screen Edge Handling</h3>
          <div className="flex justify-end">
            <HighlightTooltip
              content="This tooltip should flip to avoid going off-screen"
              position="right"
              showArrow={showArrow}
              delayShow={delayShow}
              delayHide={delayHide}
            >
              <button className="px-4 py-2 bg-green-500 text-white rounded-md">
                Edge Test
              </button>
            </HighlightTooltip>
          </div>
        </div>
        
        <div className="border p-4 rounded-lg">
          <h3 className="font-semibold mb-4">Long Content</h3>
          <div className="flex justify-center">
            <HighlightTooltip
              content={
                <div className="max-w-xs p-2">
                  <p>
                    This tooltip contains a very long paragraph that should wrap appropriately 
                    and maintain reasonable dimensions. The tooltip should handle this gracefully
                    and ensure the content remains readable without extending beyond the viewport.
                    It should also position itself optimally given the constraints.
                  </p>
                </div>
              }
              showArrow={showArrow}
              delayShow={delayShow}
              delayHide={delayHide}
            >
              <button className="px-4 py-2 bg-yellow-500 text-black rounded-md">
                Long Content
              </button>
            </HighlightTooltip>
          </div>
        </div>
        
        <div className="border p-4 rounded-lg">
          <h3 className="font-semibold mb-4">Keyboard Focus</h3>
          <div className="flex justify-center">
            <HighlightTooltip
              content="This tooltip appears on keyboard focus too!"
              showArrow={showArrow}
              delayShow={delayShow}
              delayHide={delayHide}
            >
              <button className="px-4 py-2 bg-red-500 text-white rounded-md">
                Focus Me (Tab)
              </button>
            </HighlightTooltip>
          </div>
        </div>
      </div>
      
      <footer className="mt-12 text-center text-gray-500">
        <p>Tooltip Demo Page - Showcasing the enhanced HighlightTooltip component</p>
      </footer>
    </div>
  );
};

export default TooltipDemo; 